"use client";

import { useResponsive } from "@/hooks/useResponsive";
import { animate, motion, useMotionValue, PanInfo } from "framer-motion";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import GalleryModal, { MediaItem } from "../Layouts/components/GalleryModal";
import { parseGallery } from "@/utils/parseProcessData";
import sanitizeDOMData from "@/utils/sanitizeDOMData";

export type BusinessField = {
  id: number | string;
  title: string;
  desc: string;
  image: string;
  _raw: any;
};

type SliderItem = BusinessField | { ghost: true };

type ItemProps = {
  item: BusinessField;
  isDesktop: boolean;
};

function BusinessSliderItem({ item, isDesktop }: ItemProps) {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [gallery, setGallery] = useState<MediaItem[]>([]);
  const isActive = isDesktop ? hover : true; // 🔥 key ở đây
  const handleOpen = () => {
    if (!gallery.length) {
      const parsed = parseGallery(item._raw); // 🔥 chỉ parse lúc cần
      setGallery(parsed);
    }

    setOpen(true);
  };
  return (
    <>
      <div
        className={clsx(
          "w-[440px] h-[520px] shrink-0 transition-all duration-300",
          isDesktop && "lg:hover:-translate-y-1.5",
          // ✅ mobile luôn có shadow + bg trắng
          !isDesktop && "bg-white rounded-xl shadow-md",
          "[filter:drop-shadow(0_2px_6px_rgba(0,0,0,.08))_drop-shadow(0_8px_10px_rgba(0,0,0,.14))]",
          "xl:w-[440px] xl:h-[520px]",
          "max-xl:w-[380px] max-xl:h-[490px]",
          "max-md:max-w-[280px] max-md:h-[380px]",
        )}
      >
        <div
          onMouseEnter={() => isDesktop && setHover(true)}
          onMouseLeave={() => isDesktop && setHover(false)}
          className="relative h-full flex flex-col p-6 bg-white border-t-4 border-primary overflow-hidden cursor-pointer group"
        >
          {/* overlay chỉ dùng cho desktop */}
          {isDesktop && (
            <div className="absolute inset-0 bg-primary origin-top scale-y-0 transition-transform duration-500 group-hover:scale-y-100 z-[1]" />
          )}

          <div className="relative z-10 flex flex-col h-full">
            <h3
              className={clsx(
                "text-[42px] font-black leading-[1.1] mb-6 max-md:text-[26px] max-md:mb-4",
                isDesktop
                  ? hover
                    ? "text-white"
                    : "text-black"
                  : "text-black", // mobile luôn đen
              )}
            >
              {item.title}
            </h3>

            <div className="relative flex-1 overflow-hidden flex flex-col justify-end">
              {/* image chỉ fade trên desktop */}
              <Image
                src={item.image}
                alt={item.title}
                fill
                sizes="(max-width: 768px) 280px, 440px"
                className={clsx(
                  "object-cover",
                  isDesktop && "transition-opacity duration-300",
                  isDesktop
                    ? hover
                      ? "opacity-0"
                      : "opacity-100"
                    : "opacity-100",
                )}
              />

              {/* 🔥 luôn hiện trên mobile */}
              {isActive && (
                <div className="absolute inset-0 z-20 flex flex-col justify-end gap-5 p-5 text-white">
                  {isDesktop && (
                    <p className="text-sm leading-6 line-clamp-7">
                      {item.desc}
                    </p>
                  )}
                  <span
                    onClick={() => {
                      handleOpen();
                      setHover(false);
                    }}
                    className={clsx(
                      "font-semibold",
                      isDesktop && "hover:underline",
                    )}
                  >
                    Xem chi tiết →
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <GalleryModal
        open={open}
        onClose={() => setOpen(false)}
        media={gallery}
      />
    </>
  );
}

interface BusinessSliderProps {
  businessFields: BusinessField[];
  title: string;
  desc: string;
}

export default function BusinessSlider({
  businessFields,
  title,
  desc,
}: BusinessSliderProps) {
  const { isMobile, isDesktop, isTablet } = useResponsive();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);

  const BASE_ITEM_WIDTH = 440;
  const GAP = 32;
  const COL_WIDTH = BASE_ITEM_WIDTH + GAP;

  const [containerWidth, setContainerWidth] = useState(0);
  const [currentCol, setCurrentCol] = useState(0);

  const REAL_COUNT = businessFields.length;

  const sliderItems: SliderItem[] = isDesktop
    ? [{ ghost: true }, ...businessFields, { ghost: true }]
    : businessFields;

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width);
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const maxScrollX = Math.max(
    sliderItems.length * COL_WIDTH - GAP - containerWidth,
    0,
  );

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (isMobile) return;

    const rawX = x.get();

    const clamped = Math.max(-maxScrollX, Math.min(0, rawX));

    let col = Math.round(Math.abs(clamped) / COL_WIDTH);

    if (
      Math.abs(info.offset.x) > COL_WIDTH * 0.25 ||
      Math.abs(info.velocity.x) > 600
    ) {
      col += info.offset.x < 0 ? 1 : -1;
    }

    col = Math.max(0, Math.min(col, REAL_COUNT - 1));

    const target = -col * COL_WIDTH;

    animate(x, target, {
      type: "spring",
      stiffness: 320,
      damping: 30,
    });

    setCurrentCol(col);
  };

  return (
    <div className="relative py-5 overflow-x-hidden overflow-y-visible bg-white">
      <div className="relative max-xl:flex max-xl:flex-col">
        {/* intro section */}

        <div
          className={clsx(
            "absolute inset-0 z-[1] transition-all duration-500",
            "flex items-center",
            "max-xl:relative max-xl:inset-auto",
            !isTablet && currentCol > 0 && "opacity-0 -translate-x-[120px]",
          )}
        >
          <div
            className="
            absolute left-0 right-0 top-1/2 -translate-y-1/2
            w-screen h-[66%] bg-[#f2f2f2]
            max-xl:h-full
            max-md:top-0 max-md:translate-y-0
          "
          />

          <div
            className="
            relative z-[2]
            flex flex-col justify-center
            max-w-[440px]
            pl-[80px] pr-8 py-[50px]
            text-[16px]

            max-xl:max-w-full max-xl:p-[45px] max-xl:text-[15px]
            max-lg:p-[40px] max-lg:pl-[48px] max-lg:text-[14px]
            max-md:p-[40px] max-md:text-[13px]
          "
          >
            <div className="flex gap-2 mt-6 opacity-90 font-bold text-2xl">
              <span>{title || "TIẾN ĐỘ DỰ ÁN"}</span>
            </div>

            <span>{sanitizeDOMData(desc) || "Mô tả tiến độ dự án"}</span>

            <div className="flex gap-2 mt-6 opacity-60">
              <span>← →</span>
              <small>Lướt để khám phá</small>
            </div>
          </div>
        </div>

        {/* slider */}

        <div className="relative z-[2] max-xl:-top-[70px]">
          <div
            ref={containerRef}
            className="overflow-x-hidden overflow-y-visible py-12"
          >
            <motion.div
              className="slider flex gap-8"
              drag="x"
              dragDirectionLock
              dragMomentum
              dragElastic={0.08}
              dragTransition={{ power: 0.2, timeConstant: 200 }}
              dragConstraints={{ left: -maxScrollX, right: 0 }}
              style={{ x }}
              onDragEnd={handleDragEnd}
            >
              {sliderItems.map((item, i) =>
                "ghost" in item ? (
                  <div key={i} className="w-[440px] shrink-0" />
                ) : (
                  <BusinessSliderItem
                    key={item.id}
                    item={item}
                    isDesktop={isDesktop}
                  />
                ),
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
